import React, { useEffect, useMemo, useState } from "react";
import { Target, Undo2, CircleDot } from "lucide-react";

/**
 * LiveLegTracker - Score-per-Wurf Eingabe.
 * Verwaltet ein einzelnes Leg: 501 Startscore, Wechselnde Aufnahmen,
 * automatisches Zählen von Average, 180ern, Highfinish und Ausbullen.
 * onFinish(legData) wird aufgerufen wenn das Leg beendet ist.
 */
export default function LiveLegTracker({ p1, p2, legNumber, onFinish, onCancel }) {
  const [starter, setStarter] = useState(p1.id); // wer beginnt
  const [visits, setVisits] = useState([]); // [{player_id, score, remaining_before}]
  const [current, setCurrent] = useState(""); // input
  const [remaining, setRemaining] = useState({ [p1.id]: 501, [p2.id]: 501 });
  const [turn, setTurn] = useState(p1.id);
  const [ended, setEnded] = useState(false);

  useEffect(() => { setTurn(starter); }, [starter]);

  const stats = useMemo(() => {
    const s = { [p1.id]: { score: 0, darts: 0, s180: 0 }, [p2.id]: { score: 0, darts: 0, s180: 0 } };
    for (const v of visits) {
      s[v.player_id].score += v.score;
      s[v.player_id].darts += v.darts;
      if (v.score === 180) s[v.player_id].s180 += 1;
    }
    return s;
  }, [visits, p1.id, p2.id]);

  const avgOf = (pid) => {
    const st = stats[pid];
    return st.darts > 0 ? +((st.score / st.darts) * 3).toFixed(2) : 0;
  };

  const submitVisit = (dartsUsed = 3) => {
    const val = parseInt(current);
    if (isNaN(val) || val < 0 || val > 180) return;
    const rem = remaining[turn];
    const nextRem = rem - val;
    // Bust: below 0 or exactly 1 (kein Double möglich) — für live: below 0 ist bust, remaining bleibt.
    // Wir erlauben aber alle Werte, damit auch 1-Rest möglich ist (Ausbullen-Regel)
    if (nextRem < 0) {
      // Bust - Aufnahme zählt, aber Rest bleibt
      setVisits((v) => [...v, { player_id: turn, score: 0, darts: dartsUsed, bust: true }]);
    } else {
      setVisits((v) => [...v, { player_id: turn, score: val, darts: dartsUsed, remaining_before: rem, remaining_after: nextRem }]);
      setRemaining((r) => ({ ...r, [turn]: nextRem }));
      if (nextRem === 0) {
        finishLeg(turn, val, false, dartsUsed);
        return;
      }
    }
    setCurrent("");
    setTurn((t) => (t === p1.id ? p2.id : p1.id));
  };

  const bullOut = () => {
    // Aktueller Spieler bullt aus. finish_score = aktueller remaining (vor Bull),
    // wir markieren ausgebullt=true.
    const rem = remaining[turn];
    if (rem <= 0) return;
    // wir werten Ausbullen als 3-Dart-Aufnahme (üblich Bull=50 aber wir zählen den Rest als "Score")
    setVisits((v) => [...v, { player_id: turn, score: rem, darts: 3, remaining_before: rem, remaining_after: 0, ausgebullt: true }]);
    setRemaining((r) => ({ ...r, [turn]: 0 }));
    finishLeg(turn, rem, true, 3);
  };

  const finishLeg = (winnerId, finishScore, ausgebullt, lastDarts) => {
    setEnded(true);
    // Berechnung: der Sieger wirft finishScore mit lastDarts Darts, das zählt für seinen avg
    const finalStats = { [p1.id]: { ...stats[p1.id] }, [p2.id]: { ...stats[p2.id] } };
    finalStats[winnerId] = {
      score: finalStats[winnerId].score + finishScore,
      darts: finalStats[winnerId].darts + lastDarts,
      s180: finalStats[winnerId].s180 + (finishScore === 180 ? 1 : 0),
    };
    const avg1 = finalStats[p1.id].darts ? +((finalStats[p1.id].score / finalStats[p1.id].darts) * 3).toFixed(2) : 0;
    const avg2 = finalStats[p2.id].darts ? +((finalStats[p2.id].score / finalStats[p2.id].darts) * 3).toFixed(2) : 0;
    const legData = {
      winner_id: winnerId,
      finish_score: ausgebullt ? null : finishScore,
      ausgebullt,
      avg_p1: avg1,
      avg_p2: avg2,
      s180_p1: finalStats[p1.id].s180,
      s180_p2: finalStats[p2.id].s180,
      high_finish_p1: !ausgebullt && winnerId === p1.id ? finishScore : null,
      high_finish_p2: !ausgebullt && winnerId === p2.id ? finishScore : null,
    };
    setTimeout(() => onFinish(legData), 400);
  };

  const undo = () => {
    if (visits.length === 0 || ended) return;
    const last = visits[visits.length - 1];
    setVisits((v) => v.slice(0, -1));
    if (last.remaining_before !== undefined) {
      setRemaining((r) => ({ ...r, [last.player_id]: last.remaining_before }));
    }
    setTurn(last.player_id);
    setCurrent("");
  };

  const playerCard = (p) => {
    const isTurn = turn === p.id && !ended;
    return (
      <div className={`dc-border p-6 transition-all ${isTurn ? "" : "opacity-60"}`}
        style={{ borderColor: isTurn ? "var(--dc-secondary)" : "var(--dc-border)", background: isTurn ? "rgba(35,148,88,0.06)" : "transparent" }}
        data-testid={`live-player-card-${p.id}`}>
        <div className="flex justify-between items-baseline">
          <div className="font-display text-3xl">{p.name}</div>
          {isTurn && <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--dc-secondary)" }}>am Zug</span>}
        </div>
        <div className="font-mono-data font-display text-8xl mt-2" style={{ color: remaining[p.id] === 0 ? "var(--dc-secondary)" : remaining[p.id] < 100 ? "var(--dc-chalk)" : "var(--dc-text)" }} data-testid={`live-remaining-${p.id}`}>
          {remaining[p.id]}
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3 font-mono-data text-xs">
          <div><span style={{ color: "var(--dc-muted)" }}>Avg</span> <span className="text-white">{avgOf(p.id)}</span></div>
          <div><span style={{ color: "var(--dc-muted)" }}>180</span> <span className="text-white">{stats[p.id].s180}</span></div>
          <div><span style={{ color: "var(--dc-muted)" }}>Darts</span> <span className="text-white">{stats[p.id].darts}</span></div>
        </div>
      </div>
    );
  };

  return (
    <div className="dc-border p-6 space-y-6" data-testid={`live-leg-${legNumber}`}>
      <div className="flex justify-between items-center">
        <div className="font-display text-2xl flex items-center gap-2"><Target size={18} /> Live-Tracker · Leg {legNumber}</div>
        {!ended && visits.length === 0 && (
          <div className="flex items-center gap-2 text-xs">
            <span style={{ color: "var(--dc-muted)" }}>Beginnt:</span>
            <button className={`dc-btn ${starter === p1.id ? "" : "dc-btn-ghost"}`} onClick={() => setStarter(p1.id)} data-testid={`live-starter-p1-${legNumber}`}>{p1.name}</button>
            <button className={`dc-btn ${starter === p2.id ? "" : "dc-btn-ghost"}`} onClick={() => setStarter(p2.id)} data-testid={`live-starter-p2-${legNumber}`}>{p2.name}</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {playerCard(p1)}
        {playerCard(p2)}
      </div>

      {!ended && (
        <div className="dc-border p-4 space-y-3">
          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--dc-muted)" }}>Aufnahme (3 Darts)</div>
          <div className="flex gap-2">
            <input
              type="number"
              min="0"
              max="180"
              className="dc-input font-mono-data text-2xl text-center"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitVisit()}
              placeholder="Score"
              autoFocus
              data-testid={`live-visit-input-${legNumber}`}
            />
            <button className="dc-btn dc-btn-secondary" onClick={() => submitVisit()} data-testid={`live-visit-submit-${legNumber}`}>
              Aufnahme
            </button>
            <button className="dc-btn dc-btn-ghost" onClick={undo} disabled={visits.length === 0} data-testid={`live-undo-${legNumber}`}>
              <Undo2 size={14} />
            </button>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            {[26, 41, 45, 60, 85, 100, 121, 140, 180].map((preset) => (
              <button key={preset} className="dc-btn dc-btn-ghost px-2 py-1" onClick={() => setCurrent(String(preset))} data-testid={`live-preset-${preset}-${legNumber}`}>
                {preset}
              </button>
            ))}
          </div>
          <div className="flex justify-between items-center pt-2 border-t dc-border">
            <button className="dc-btn dc-btn-ghost text-xs" onClick={onCancel} data-testid={`live-cancel-${legNumber}`}>
              Abbrechen
            </button>
            <button className="dc-btn text-xs" style={{ background: "var(--dc-chalk)", color: "#000", borderColor: "var(--dc-chalk)" }} onClick={bullOut} data-testid={`live-bullout-${legNumber}`}>
              <CircleDot size={12} /> Ausgebullt
            </button>
          </div>
        </div>
      )}

      {ended && (
        <div className="dc-border p-4 text-center" style={{ background: "var(--dc-secondary)", color: "white" }}>
          <div className="font-display text-2xl">Leg beendet!</div>
        </div>
      )}

      {visits.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--dc-muted)" }}>Verlauf ({visits.length} Aufnahmen)</div>
          <div className="flex flex-wrap gap-1 font-mono-data text-xs max-h-24 overflow-y-auto">
            {visits.map((v, i) => (
              <span key={i} className="px-2 py-1 dc-border" style={{ color: v.player_id === p1.id ? "var(--dc-primary)" : "var(--dc-secondary)" }}>
                {v.bust ? "✗" : v.ausgebullt ? `Bull(${v.score})` : v.score}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
